import React from 'react';
import {
  IconButtonProps as MuiIconButtonProps,
  SxProps,
  Theme,
} from '@mui/material';

import { IconButton as MuiIconButton } from '@mui/material';
import { mergeSx } from '@client/helpers/mui.helpers';
import { Icon, type IconCategory, type IconProps } from '@ui/Icon';

// This wrapper intentionally uses MUI's IconButton under the hood.
// Local alias to keep usage centralized in this file.
const MuiIconButtonWrapper: typeof MuiIconButton = MuiIconButton;

export type IconButtonProps<Category extends IconCategory = IconCategory> = {
  category: Category;
  icon: IconProps<Category>['icon'];
  size?: IconProps<Category>['size'];
  sx?: SxProps<Theme>;
  shape?: 'square' | 'circle';
  /** @defaultValue 'default' */
  variant?: IconButtonVariant;
  hidden?: boolean;
  disabled?: boolean;
  id?: string;
  rotateIcon?: number;
  disableRipple?: boolean;
  onClick: MuiIconButtonProps['onClick'];
  onKeyDown?: MuiIconButtonProps['onKeyDown'];
  /** Accessible name of the button — an icon alone leaves it unnamed. */
  'aria-label'?: string;
  /**
   * Marks the rendered button so an inline-edit input's blur handler keeps editing
   * alive when focus lands here (see {@link InlineTextField}), letting the button's
   * own {@link onClick} run instead of the input tearing it down first.
   */
  'data-keep-inline-edit'?: boolean;
};

const iconButtonTransitionSx = {
  transition: 'all 0.2s ease-in-out',
} as const;

/**
 * MUI-backed icon button with Nodon {@link Icon} registry. Use {@link IconButtonVariant} for preset surfaces.
 * Forwards its ref — a `Tooltip` parent needs the DOM node to anchor to.
 */
const IconButtonInner = <Category extends IconCategory = IconCategory>({
  size,
  category,
  icon,
  hidden = false,
  disabled = false,
  disableRipple = false,
  rotateIcon,
  sx,
  shape = 'circle',
  variant = 'default',
  id,
  onClick,
  onKeyDown,
  buttonRef,
  ...props
}: IconButtonProps<Category> & {
  buttonRef?: React.ForwardedRef<HTMLButtonElement>;
}) => {
  return (
    <MuiIconButtonWrapper
      ref={buttonRef}
      id={id}
      size="small"
      disabled={disabled}
      disableRipple={disableRipple}
      sx={mergeSx(
        iconButtonTransitionSx,
        {
          borderRadius: (theme) =>
            shape === 'square' ? `${theme.shape.borderRadius}px` : undefined,
          visibility: hidden ? 'hidden' : 'visible',
          pointerEvents: hidden ? 'none' : 'auto',
        },
        iconButtonVariantSx[variant],
        sx,
      )}
      onClick={onClick}
      onKeyDown={onKeyDown}
      {...props}
    >
      <Icon category={category} icon={icon} size={size} rotate={rotateIcon} />
    </MuiIconButtonWrapper>
  );
};

export const IconButton = React.forwardRef(function IconButton<
  Category extends IconCategory = IconCategory,
>(
  props: IconButtonProps<Category>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  return <IconButtonInner {...props} buttonRef={ref} />;
  // `forwardRef` erases the generic parameter, so re-apply it here. The runtime
  // shape is unchanged — this cast only restores the call signature.
}) as unknown as <Category extends IconCategory = IconCategory>(
  props: IconButtonProps<Category> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactElement;

export type IconButtonVariant = 'default' | 'overlay' | 'overlayActive';

const iconButtonVariantSx = {
  default: {
    '&:hover': {
      backgroundColor: 'transparent',
      color: 'rgba(0,0,0,1)', // as per ticket feedback
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'rgba(0,0,0,.15)',
    },
  },
  /** White tile, dark icon, elevation — e.g. floating controls on the design map. */
  overlay: {
    backgroundColor: (theme: Theme) => theme.palette.common.white,
    boxShadow: (theme: Theme) => theme.shadows[4],
    color: (theme: Theme) => theme.palette.grey[900],
    '&:hover': (theme: Theme) => ({
      // Solid light grey — action.hover is too transparent on white overlay tiles.
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.grey[900],
    }),
    '&.Mui-focusVisible': {
      backgroundColor: 'rgba(0,0,0,.15)',
    },
  },
  /** Overlay tile in the pressed/active tool state (e.g. add-building pen). */
  overlayActive: {
    backgroundColor: (theme: Theme) => theme.palette.primary.main,
    boxShadow: (theme: Theme) => theme.shadows[4],
    color: (theme: Theme) => theme.palette.primary.contrastText,
    '&:hover': (theme: Theme) => ({
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.primary.contrastText,
    }),
    '&.Mui-focusVisible': {
      backgroundColor: (theme: Theme) => theme.palette.primary.dark,
    },
  },
} satisfies Record<IconButtonVariant, SxProps<Theme>>;
